import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, X, ShieldCheck, BadgeCheck, Sparkles, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { track } from "../lib/analytics";

export default function PaymentModal({ open, onClose, sessionId, tier, onSuccess }) {
  const [stage, setStage] = useState("confirm"); // confirm | processing | success
  const [order, setOrder] = useState(null);

  if (!open) return null;
  const amount = tier === "basic" ? 49 : 444;
  const title = tier === "basic" ? "Unlock Full Report" : "Unlock Career Blueprint";

  const startPayment = async () => {
    try {
      setStage("processing");
      track("payment_initiated", { tier, value: amount, currency: "INR" });
      const lang = (typeof window !== "undefined" && localStorage.getItem("bharatiq_lang")) || "en";
      const r = await api.post("/payments/create-order", { session_id: sessionId, tier, lang });
      setOrder(r.data);
      // Simulate Razorpay checkout latency
      await new Promise((res) => setTimeout(res, 1500));
      // Mock verify
      const v = await api.post("/payments/verify", {
        payment_id: r.data.payment_id,
        provider_payment_id: `pay_mock_${Date.now()}`,
      });
      if (v.data.status === "success" || v.data.status === "already_paid") {
        setStage("success");
        track(tier === "premium" ? "premium_purchase" : "payment_success", { tier, value: amount, currency: "INR" });
        toast.success("Payment successful");
        setTimeout(() => { onSuccess && onSuccess(); }, 1100);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
      setStage("confirm");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-primary/60 backdrop-blur-sm grid place-items-center p-4"
        data-testid="payment-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          className="w-full max-w-md bg-white rounded-xl border border-border shadow-2xl overflow-hidden"
        >
          <div className={`p-5 flex items-center justify-between ${tier === "premium" ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground"}`}>
            <div className="flex items-center gap-2.5">
              {tier === "premium" ? <Sparkles className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              <span className="font-display font-bold">{title}</span>
            </div>
            <button onClick={onClose} className="p-1 rounded hover:bg-white/15" data-testid="payment-modal-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6">
            {stage === "confirm" && (
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl font-extrabold">₹{amount}</span>
                  <span className="text-sm text-muted-foreground">one-time</span>
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  {(tier === "basic"
                    ? ["Full IQ + Personality + EQ scores", "Top 5 career fits", "Personalized strengths & gaps", "30-day next steps"]
                    : ["Everything in Basic Report", "6-month roadmap", "Skill gap analysis", "AI Career Advisor (chat)"])
                    .map((t, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <BadgeCheck className={`w-4 h-4 ${tier === "premium" ? "text-accent" : "text-primary"}`} />
                        <span>{t}</span>
                      </div>
                    ))}
                </div>
                <button
                  onClick={startPayment}
                  data-testid="payment-pay-btn"
                  className={`mt-6 w-full py-3 rounded-md font-semibold ${tier === "premium" ? "btn-accent" : "btn-primary"}`}
                >
                  Pay ₹{amount}
                </button>
                <div className="mt-3 text-[11px] text-muted-foreground flex items-center justify-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5" /> Razorpay · Secure · UPI · Cards
                </div>
              </div>
            )}
            {stage === "processing" && (
              <div className="py-10 text-center">
                <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin" />
                <div className="mt-4 font-display text-lg font-bold">Processing your payment</div>
                <div className="mt-1 text-sm text-muted-foreground">Generating your report — this takes 10–15 seconds.</div>
              </div>
            )}
            {stage === "success" && (
              <div className="py-10 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-primary text-primary-foreground grid place-items-center">
                  <BadgeCheck className="w-7 h-7" />
                </div>
                <div className="mt-4 font-display text-lg font-bold">Payment successful</div>
                <div className="mt-1 text-sm text-muted-foreground">Unlocking your report…</div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
